import React from "react";
import { useTranslation } from "react-i18next";
import { StyledModal, StyledH2, StyledLabel } from "@aureskonnect/react-ui";
import { useSnapshot } from "valtio";
import { ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { toast } from "react-toastify";

import { capitalize } from "@helpers/general";
import { store } from "@components/VerticalLayout/store";
import { store as storeGlobal, setIsLoading, setKey } from "../store";

import { CustomMainColorButton } from "@components/Common/CustomMainColorButton";
import { CustomSecondaryColorButton } from "@components/Common/CustomSecondaryColorButton";
import { showSuccessToast } from "@components/Common/SuccessToast";
import ErrorToast from "@components/Common/ErrorTost";
import { useFetchGlobal } from "../../../../api/hooks";
import { uuid } from "uuidv4";
import { editSaleSupportRequest, addTagRequest } from "./helper";
import { ConfirmationModificationMessageType } from "../types/index";

export function ConfirmationModificationModal({
    isModalOpened,
    setIsModalOpened,
    data,
    setIsAddSaleSupportClicked,
    setIsEdited,
    setSaleSupportData,
    resetData,
    saleSupport,
}: ConfirmationModificationMessageType) {
    const { t } = useTranslation();
    const { fetchGlobal } = useFetchGlobal();

    const { userID, franchiseID, operatorID } = useSnapshot(store);
    const uuidUser = localStorage.getItem("uuidUser");
    const { shopsSaleSupportData } = useSnapshot(storeGlobal);

    async function editSaleSupport() {
        setIsLoading(true);
        toast.dismiss();

        try {
            await editSaleSupportRequest(
                fetchGlobal,
                saleSupport,
                data,
                shopsSaleSupportData,
                userID,
                franchiseID,
                operatorID,
                uuidUser
            );

            showSuccessToast(t("The sale support is changed successfully"));
            setIsAddSaleSupportClicked(false);
            setIsEdited(false);
            setIsModalOpened(false);
            setSaleSupportData([]);
            resetData();
            setKey(uuid());
        } catch (err) {
            ErrorToast(t("There's an error"));
        } finally {
            setIsLoading(false);
        }
    }

    async function addTag() {
        try {
            await addTagRequest(
                fetchGlobal,
                data,
                userID,
                operatorID,
                uuidUser,
                franchiseID
            );
        } catch (e) {
            ErrorToast(t("There's an error"));
        }
    }

    return (
        <StyledModal toggle={true} isOpen={isModalOpened!} centered>
            <ModalHeader
                className="text-capitalize"
                toggle={() => setIsModalOpened(!isModalOpened)}
            >
                <StyledH2>{capitalize(t("Alert"))}</StyledH2>
            </ModalHeader>
            <ModalBody>
                <StyledLabel
                    className="pl-3 m-0"
                    style={{ whiteSpace: "pre-line" }}
                >
                    {t(
                        `Do you want to apply these changes for all stores? \n Attention ! If you click on "Validate", the specific settings of the shops will be modified.`
                    )}
                </StyledLabel>
            </ModalBody>
            <ModalFooter className="border-top-0 ">
                <CustomSecondaryColorButton
                    outline
                    variant="light"
                    onClick={() => {
                        setIsModalOpened(!isModalOpened);
                        setIsAddSaleSupportClicked(false);
                        setIsEdited(false);
                    }}
                    rounded
                >
                    {t("Cancel")}
                </CustomSecondaryColorButton>
                <CustomMainColorButton
                    rounded
                    className="mr-2"
                    variant="primary"
                    onClick={() => {
                        editSaleSupport();
                        addTag();
                        setIsModalOpened(!isModalOpened);
                    }}
                >
                    {t("Validate")}
                </CustomMainColorButton>
            </ModalFooter>
        </StyledModal>
    );
}
